import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import {
  ProductPdfExportsFilterParams,
  ProductPdfExportType,
  ProductPdfExportsListResponse,
  CreateProductPdfExportParams
} from '@/types/product-pdf-export'
import { BulkDeleteResponse } from '@/types/general'
import { getErrorMessage } from '@/lib/utils'
import { SortEnum } from '@/enums'

import {
  fetchProductPdfExports,
  fetchProductPdfExport,
  createProductPdfExport,
  deleteProductPdfExport,
  bulkDeleteProductPdfExports
} from '@/lib/api'

type ProductPdfExportStateType = {
  data: ProductPdfExportType | null
  isLoading: boolean
  error: string | null
}

type ProductPdfExportsListStateType = {
  data: ProductPdfExportsListResponse | null
  isLoading: boolean
  error: string | null
}

type ProductPdfExportsStoreState = {
  productPdfExport: ProductPdfExportStateType
  productPdfExportsList: ProductPdfExportsListStateType
  filters: ProductPdfExportsFilterParams
  selectedProductPdfExportIds: string[]
  setFilters: (filters: Partial<ProductPdfExportsFilterParams>) => void
  resetFilters: () => void
  updateUrlParams: (searchParams: URLSearchParams) => void
  getUrlParams: () => URLSearchParams

  fetchProductPdfExports: (params?: ProductPdfExportsFilterParams) => Promise<ProductPdfExportsListResponse>
  resetProductPdfExportsList: () => void

  getProductPdfExport: (id: string) => Promise<ProductPdfExportType>
  createProductPdfExport: (params: CreateProductPdfExportParams) => Promise<ProductPdfExportType>
  deleteProductPdfExport: (id: string) => Promise<void>
  bulkDeleteProductPdfExports: (ids: string[]) => Promise<BulkDeleteResponse>

  selectProductPdfExport: (id: string) => void
  deselectProductPdfExport: (id: string) => void
  toggleProductPdfExportSelection: (id: string) => void
  selectAllProductPdfExports: (productPdfExports: ProductPdfExportType[]) => void
  deselectAllProductPdfExports: () => void
  isProductPdfExportSelected: (id: string) => boolean
  resetProductPdfExport: () => void
}

const defaultFilters: ProductPdfExportsFilterParams = {
  sort: SortEnum.DESC,
  sortBy: 'createdAt',
  q: '',
  createdUsers: undefined,
  updatedUsers: undefined,
  createdAtStart: undefined,
  createdAtEnd: undefined,
  updatedAtStart: undefined,
  updatedAtEnd: undefined
}

const useProductPdfExportsStore = create<ProductPdfExportsStoreState>()(
  devtools((set, get) => ({
    productPdfExport: { data: null, isLoading: false, error: null },
    productPdfExportsList: { data: null, isLoading: false, error: null },
    selectedProductPdfExportIds: [],
    filters: defaultFilters,

    setFilters: filters => set(state => ({ filters: { ...state.filters, ...filters } })),
    resetFilters: () => set({ filters: defaultFilters }),

    updateUrlParams: searchParams => {
      const filters: Partial<ProductPdfExportsFilterParams> = {}
      for (const [key, value] of searchParams.entries()) {
        if (key === 'sort') {
          filters[key] = value as SortEnum
        } else if (key === 'createdUsers' || key === 'updatedUsers') {
          filters[key] = value.split(',')
        } else {
          filters[key as keyof ProductPdfExportsFilterParams] = value as any
        }
      }
      set({ filters })
    },

    getUrlParams: () => {
      const params = new URLSearchParams()
      const { filters } = get()
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if ((key === 'createdUsers' || key === 'updatedUsers') && Array.isArray(value)) {
            params.set(key, value.join(','))
          } else {
            params.set(key, String(value))
          }
        }
      })
      return params
    },

    getProductPdfExport: async id => {
      set(state => ({ productPdfExport: { ...state.productPdfExport, isLoading: true, error: null } }))
      try {
        const data = await fetchProductPdfExport(id)
        set(state => ({ productPdfExport: { ...state.productPdfExport, data, isLoading: false } }))
        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)
        set(state => ({ productPdfExport: { ...state.productPdfExport, error: errorMessage, isLoading: false } }))
        throw error
      }
    },

    createProductPdfExport: async params => {
      set(state => ({ productPdfExport: { ...state.productPdfExport, isLoading: true, error: null } }))
      try {
        const data = await createProductPdfExport(params)
        set(state => ({ productPdfExport: { ...state.productPdfExport, data, isLoading: false } }))
        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)
        set(state => ({ productPdfExport: { ...state.productPdfExport, error: errorMessage, isLoading: false } }))
        throw error
      }
    },

    deleteProductPdfExport: async id => {
      set(state => ({ productPdfExport: { ...state.productPdfExport, isLoading: true, error: null } }))
      try {
        await deleteProductPdfExport(id)
        set(state => ({ productPdfExport: { ...state.productPdfExport, isLoading: false } }))
      } catch (error) {
        const errorMessage = getErrorMessage(error)
        set(state => ({ productPdfExport: { ...state.productPdfExport, error: errorMessage, isLoading: false } }))
        throw error
      }
    },

    resetProductPdfExport: () =>
      set(state => ({ productPdfExport: { ...state.productPdfExport, data: null, error: null } })),

    selectProductPdfExport: id =>
      set(state =>
        state.selectedProductPdfExportIds.includes(id)
          ? state
          : { selectedProductPdfExportIds: [...state.selectedProductPdfExportIds, id] }
      ),

    deselectProductPdfExport: id =>
      set(state => ({
        selectedProductPdfExportIds: state.selectedProductPdfExportIds.filter(i => i !== id)
      })),

    toggleProductPdfExportSelection: id =>
      set(state => ({
        selectedProductPdfExportIds: state.selectedProductPdfExportIds.includes(id)
          ? state.selectedProductPdfExportIds.filter(i => i !== id)
          : [...state.selectedProductPdfExportIds, id]
      })),

    selectAllProductPdfExports: productPdfExports =>
      set({ selectedProductPdfExportIds: productPdfExports.map(e => e.id) }),

    deselectAllProductPdfExports: () => set({ selectedProductPdfExportIds: [] }),

    isProductPdfExportSelected: id => get().selectedProductPdfExportIds.includes(id),

    bulkDeleteProductPdfExports: async ids => {
      set(state => ({ productPdfExport: { ...state.productPdfExport, isLoading: true, error: null } }))
      try {
        const response = await bulkDeleteProductPdfExports({ ids })
        set(state => ({
          productPdfExport: { ...state.productPdfExport, isLoading: false },
          selectedProductPdfExportIds: state.selectedProductPdfExportIds.filter(
            id => !response.deletedItems.includes(id)
          )
        }))
        return response
      } catch (error) {
        const errorMessage = getErrorMessage(error)
        set(state => ({ productPdfExport: { ...state.productPdfExport, error: errorMessage, isLoading: false } }))
        throw error
      }
    },

    fetchProductPdfExports: async (params?: ProductPdfExportsFilterParams) => {
      const filtersToUse = params || get().filters
      set(state => ({ productPdfExportsList: { ...state.productPdfExportsList, isLoading: true, error: null } }))
      try {
        const data = await fetchProductPdfExports(filtersToUse)
        set(state => ({ productPdfExportsList: { ...state.productPdfExportsList, data, isLoading: false } }))
        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)
        set(state => ({
          productPdfExportsList: { ...state.productPdfExportsList, error: errorMessage, isLoading: false }
        }))
        throw error
      }
    },

    resetProductPdfExportsList: () =>
      set(state => ({ productPdfExportsList: { ...state.productPdfExportsList, data: null, error: null } }))
  }))
)

export default useProductPdfExportsStore
